import { motion } from 'framer-motion'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'i18n'
import { Box, useMediaQuery } from '@mui/material'
import { CorrectAnswer } from './CorrectAnswer'
import Accordion from 'components/Accordion/Accordion'
import AnimatedScaleOption from 'components/AnimatedScaleOption'

import { ArrowRight } from 'lucide-react'

import * as styles from './Question.module.scss'
import getClassNameFactory from 'util/getClassNameFactory'

const getClassName = getClassNameFactory('Question', styles)
const getTitleClassName = getClassNameFactory('QuestionTitle', styles)
const getAnswerClassName = getClassNameFactory('AnswerWrapper', styles)
const getAccordionClassName = getClassNameFactory('Accordion', styles)
const getAnswersGridClassName = getClassNameFactory('AnswersGrid', styles)
const getAnswersReorderClassName = getClassNameFactory('AnswerReorder', styles)
const getSubmitButtonClassName = getClassNameFactory('SubmitButton', styles)

const spring = {
  type: 'spring',
  damping: 25,
  stiffness: 120,
}

export const Question = ({
  question,
  onSubmit,
  onSelectAnswer,
  onNextQuestion,
  selectedAnswers,
  showRightAnswer,
  isLoading,
  hasStarted,
  currentQuestionIndex,
  triviaCopy,
  sponsoredType,
  isLastQuestion,
}) => {
  const { t } = useTranslation(['textTrivia', 'common'])
  const isMobile = useMediaQuery('(max-width: 768px)')

  const handleChangeOption = useCallback(
    answerId => {
      onSelectAnswer(answerId)
    },
    [onSelectAnswer],
  )

  const handleSubmit = useCallback(async () => {
    onSubmit(selectedAnswers)
  }, [onSubmit, selectedAnswers])

  const handleNextQuestion = useCallback(() => {
    onNextQuestion()
  }, [onNextQuestion])

  const getSelectedAnswer = useCallback(
    (selectedAnswers, answer, questionId) => {
      return selectedAnswers[questionId] === answer.id
    },
    [],
  )

  const getCheckboxItem = useCallback(
    ({
      answer,
      index,
      selectedAnswers,
      handleChangeOption,
      isLoading,
      showRightAnswer,
      response,
      sponsoredType,
    }) => {
      const isSelected = getSelectedAnswer(selectedAnswers, answer, question.id)

      const showIcon = answerId => {
        if (showRightAnswer) {
          return (
            answerId === question.correctAnswerId ||
            answerId === selectedAnswers[question.id]
          )
        }
        return false
      }

      return {
        label: (
          <AnimatedScaleOption
            answerId={answer.id}
            option={answer.text}
            image={answer.image}
            index={index}
            onChange={
              !showRightAnswer ? () => handleChangeOption(answer.id) : undefined
            }
            selected={isSelected}
            isEditing={!showRightAnswer}
            labelClassName={getClassName({
              withImage: !!answer.image,
              isCorrect:
                showRightAnswer && answer.id === question.correctAnswerId,
              isIncorrect:
                showRightAnswer && answer.id !== question.correctAnswerId,
              showRightAnswer: showRightAnswer,
              counterActive: hasStarted,
              sponsored: sponsoredType !== 'sponsored',
            })}
            className={getAnswerClassName({
              isEditing: isSelected && !showRightAnswer,
              isEditingSponsored:
                isSelected && !showRightAnswer && sponsoredType !== 'sponsored',
              isPicking: !showRightAnswer,
              isPickingSponsored:
                !showRightAnswer && sponsoredType !== 'sponsored',
            })}
            hasStarted={hasStarted}
            isLoading={isLoading}
            showRightAnswer={showRightAnswer}
            correctAnswerId={question.correctAnswerId}
            showIcon={showIcon(answer.id)}
          />
        ),
        content: (
          <CorrectAnswer
            sponsoredType={sponsoredType}
            answerHasImage={!!answer.image}
            correctAnswerCtaText={response?.ctaText}
            correctAnswerCtaUrl={response?.ctaUrl}
            correctAnswerImage={response?.image}
            correctAnswerText={response?.text}
          />
        ),
        showArrow: false,
        isOpen: showRightAnswer && answer.id === question.correctAnswerId,
      }
    },
    [getSelectedAnswer, question.correctAnswerId, question.id, hasStarted],
  )

  const mappedAnswers = useMemo(() => {
    if (!question) return null

    return question.answers.map((answer, index) => {
      const isCorrectAnswer = answer?.id === question.correctAnswerId
      const shouldReorder =
        !isMobile &&
        showRightAnswer &&
        question.answers[0].image &&
        question.correctAnswerId

      return (
        <motion.div
          layout
          transition={spring}
          key={answer?.id}
          className={
            shouldReorder
              ? getAnswersReorderClassName({
                  isCorrect: isCorrectAnswer,
                  isIncorrect: !isCorrectAnswer,
                })
              : undefined
          }
        >
          <Accordion
            items={[
              getCheckboxItem({
                answer,
                index,
                selectedAnswers,
                handleChangeOption,
                showRightAnswer,
                sponsoredType,
                response: {
                  ctaText: question.ctaCopy,
                  ctaUrl: question.ctaUrl,
                  image: question.textAnsweredImage,
                  text: question.textAnswered,
                },
                isLoading,
              }),
            ]}
            tabLabelClassName={getAccordionClassName({
              withImage: !!answer.image,
            })}
            showArrow={false}
          />
        </motion.div>
      )
    })
  }, [
    question,
    selectedAnswers,
    handleChangeOption,
    isLoading,
    isMobile,
    sponsoredType,
    showRightAnswer,
    getCheckboxItem,
  ])

  if (!question) return 'No question'

  const isDisabled =
    !showRightAnswer &&
    Object.keys(selectedAnswers).length !== currentQuestionIndex + 1

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '4px',
      }}
    >
      <h3
        className={getTitleClassName({
          sponsored: !!sponsoredType,
        })}
        dangerouslySetInnerHTML={{
          __html: question.text || question.question || 'Question Title',
        }}
      />
      {sponsoredType && (
        <span
          className={getClassName('triviaCopy')}
          dangerouslySetInnerHTML={{
            __html: triviaCopy,
          }}
        />
      )}
      <div
        className={getAnswersGridClassName({
          showCorrect:
            showRightAnswer &&
            question.answers[0].image &&
            question.correctAnswerId,
          hasImage: question.answers[0].image,
        })}
      >
        {mappedAnswers}
      </div>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          marginTop: '20px',
        }}
      >
        <button
          type="submit"
          className={getSubmitButtonClassName({
            blurred: !hasStarted,
          })}
          disabled={isDisabled}
          onClick={showRightAnswer ? handleNextQuestion : handleSubmit}
        >
          {showRightAnswer ? (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {isLastQuestion
                ? t('textTrivia:showResults')
                : t('textTrivia:nextQuestion')}{' '}
              <ArrowRight size={20} />
            </span>
          ) : (
            t('common:submit')
          )}
        </button>
      </Box>
    </div>
  )
}
